import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Loader2, Copy, ArrowRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import Markdown from "@/components/Markdown";
import { Skeleton } from "@/components/Skeleton";
import { toast } from "sonner";

/**
 * InlineSuggestionPanel — runs an AI suggestion in-place on the dashboard
 * without page navigation. Powers the "Use this suggestion" inline UX.
 *
 * Modes:
 *   - "lesson_plan": calls /api/ai/generate/lesson-plan with default settings (preview)
 *   - "ocr_quiz": calls /api/ocr/action with action=quiz on the provided document_id
 *   - "open_teaching": just navigates (no inline gen, but UI still consistent)
 */
export default function InlineSuggestionPanel({ suggestion, onClose }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [output, setOutput] = useState(null);

  useEffect(() => {
    // Auto-run when panel opens, unless it's a pure navigation
    if (!suggestion || suggestion.action === "open_teaching") return;
    run();
    // eslint-disable-next-line
  }, [suggestion]);

  if (!suggestion) return null;

  const run = async () => {
    setBusy(true);
    setOutput(null);
    try {
      if (suggestion.action === "lesson_plan") {
        const { data } = await api.post("/ai/generate/lesson-plan", {
          subject: "Science",
          grade: "8",
          topic: "Photosynthesis",
          duration_minutes: 45,
          curriculum: "CBSE",
          language: "English",
        });
        setOutput({ title: data.title, content: data.content });
      } else if (suggestion.action === "ocr_quiz") {
        if (!suggestion.document_id) {
          toast.error("Document not found for this suggestion");
          onClose();
          return;
        }
        const { data } = await api.post("/ocr/action", {
          document_id: suggestion.document_id,
          action: "quiz",
        });
        setOutput({ title: data.title, content: data.content });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Generation failed");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!output) return;
    try { await navigator.clipboard.writeText(output.content); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };

  // Pure-nav fallback
  if (suggestion.action === "open_teaching") {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 tb-fade-in" data-testid="inline-suggestion-panel">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl tb-card p-6 sm:p-7 tb-fade-up max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between mb-4 gap-3">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-primary inline-flex items-center gap-2">
              <Sparkles className="h-3 w-3" /> AI suggestion
            </p>
            <h3 className="mt-1.5 font-display text-lg sm:text-xl font-medium tracking-tight">{suggestion.label}</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">{suggestion.hint}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="rounded-full shrink-0" aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-auto rounded-xl bg-muted/30 p-4 border border-border/60 min-h-[160px]">
          {busy && (
            <div className="space-y-2 tb-tracing-border rounded-xl p-2">
              <Skeleton className="h-3 w-2/3" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-5/6" />
              <Skeleton className="h-3 w-4/6" />
              <Skeleton className="h-3 w-3/4" />
            </div>
          )}
          {!busy && output && <Markdown>{output.content}</Markdown>}
          {!busy && !output && <p className="text-sm text-muted-foreground text-center py-6">Tap regenerate to retry.</p>}
        </div>

        <div className="mt-4 flex flex-wrap gap-2 justify-end">
          <Button variant="ghost" className="rounded-full" onClick={onClose} data-testid="suggestion-close">Close</Button>
          {output && (
            <>
              <Button variant="outline" className="rounded-full" onClick={copy} data-testid="suggestion-copy">
                <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
              </Button>
              <Button variant="outline" className="rounded-full" onClick={run} disabled={busy} data-testid="suggestion-regenerate">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
                Regenerate
              </Button>
            </>
          )}
          <Button className="rounded-full" onClick={() => {
            onClose();
            if (suggestion.action === "lesson_plan") navigate("/teaching-assistant?tool=lesson_plan");
            else if (suggestion.action === "ocr_quiz") navigate("/ocr");
          }} data-testid="suggestion-open-full">
            Open full editor <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}
